import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JSONSchema7 } from 'json-schema';

import { api } from '@/lib/api-client';

import { getItemTemplateSchemaQueryKey } from './get-item-template-schema';
import { getItemTemplateSchemasQueryKey } from './get-item-template-schemas';
import { getItemTemplateVersionsQueryKey } from './get-item-template-versions';
import { getItemTemplatesQueryKey } from './get-item-templates';

export interface UpdateItemTemplateDto {
  name: string;
  templateKey: string;
  jsonSchema: JSONSchema7;
}

export const updateItemTemplateVersion = async (
  orgId: string,
  itemTemplateId: string,
  data: UpdateItemTemplateDto,
): Promise<unknown> => {
  const { data: result } = await api.patch<unknown, ApiResponse<unknown>>(
    `/orgs/${orgId}/item-templates/${itemTemplateId}`,
    data,
  );
  return result;
};

interface UseUpdateItemTemplateOptions {
  mutationConfig?: MutationConfig<(data: UpdateItemTemplateDto) => Promise<unknown>>;
}

export const useUpdateItemTemplate = (
  itemTemplateId: string,
  { mutationConfig }: UseUpdateItemTemplateOptions = {},
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: getItemTemplatesQueryKey(orgId) });
      void queryClient.invalidateQueries({ queryKey: getItemTemplateSchemasQueryKey(orgId) });
      void queryClient.invalidateQueries({
        queryKey: getItemTemplateSchemaQueryKey(orgId, itemTemplateId),
      });
      void queryClient.invalidateQueries({
        queryKey: getItemTemplateVersionsQueryKey(orgId, itemTemplateId),
      });
      notifications.success('itemTemplates.edit.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('itemTemplates.edit.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (data: UpdateItemTemplateDto) =>
      updateItemTemplateVersion(orgId, itemTemplateId, data),
  });
};
